import { supabase, unwrap } from '../supabase';
import type { AiAnalysis, AiRecord } from '../types';

const TERMINAL = ['completed', 'skipped', 'failed', 'insufficient credits', 'reattempt failed'];

export interface AiServiceStatus { paused: boolean; errorCode: string | null; message: string | null; pausedAt: string | null; }
export interface AiFailureLog { id: number; attempt: number; error_code: string; message: string; provider_http_status: number | null; terminal: boolean; created_at: string; }

export async function getServiceStatus(): Promise<AiServiceStatus> {
  return unwrap(await supabase.rpc('get_ai_service_status')) as AiServiceStatus;
}

export async function listFailures(orgId: string, jobId: string | number): Promise<AiFailureLog[]> {
  return (unwrap(await supabase.from('ai_failure_logs')
    .select('id,attempt,error_code,message,provider_http_status,terminal,created_at')
    .eq('org_id', orgId).eq('ai_job_id', jobId).order('created_at', { ascending: false }).limit(20)) ?? []) as AiFailureLog[];
}

export function isAiSettled(record: Pick<AiRecord, 'status' | 'errorMessage'> | null): boolean {
  if (!record) return true;
  if (TERMINAL.includes(String(record.status).toLowerCase())) return true;
  return /insufficient credits/i.test(record.errorMessage || '');
}

export function aiErrorText(record: Pick<AiRecord, 'status' | 'errorMessage'>): string | null {
  const status = String(record.status).toLowerCase();
  if (status === 'completed') return null;
  if (status === 'insufficient credits' || /insufficient credits/i.test(record.errorMessage || ''))
    return 'Not enough AI credits';
  if (status === 'skipped') return record.errorMessage || 'Skipped — the recording had no usable speech';
  if (status === 'failed' || status === 'reattempt failed') return record.errorMessage || 'Analysis failed';
  return null;
}

const n = (v: unknown) => (v === null || v === undefined ? null : Number(v));

function mapRecord(row: Record<string, unknown>, call?: Record<string, unknown> | null): AiRecord {
  const c = call ?? (row.call_log as Record<string, unknown> | null) ?? null;
  const raw = row.analysis;
  let analysis: AiAnalysis | null = null;
  if (typeof raw === 'string') {
    try {
      analysis = JSON.parse(raw) as AiAnalysis;
    } catch {
      analysis = { transcription: raw };
    }
  } else if (raw && typeof raw === 'object') {
    analysis = raw as AiAnalysis;
  }
  return {
    analysisVersion: (row.analysis_version as string) || undefined,
    id: row.id as number | string,
    callId: Number(row.call_id),
    status: String(row.status || 'pending'),
    analysis,
    errorMessage: (row.error_message as string) || null,
    creditsUsed: n(row.credit_used),
    duration: n(row.duration),
    createdAt: (row.created_at as string) || null,
    call: c
      ? {
          callTime: (c.call_time as string) || null,
          phone: String(c.caller_phone ?? ''),
          name: (c.caller_name as string) || null,
          duration: Number(c.duration) || 0,
          recordingUrl: (c.recording_url as string) || null,
        }
      : null,
  };
}

export async function listAnalyses(orgId: string, limit = 100, previous?: AiRecord[]): Promise<AiRecord[]> {
  // First paint uses the original single request. Subsequent polls compare small
  // fingerprints instead of transferring every transcript again.
  if (previous?.length) {
    const snapshot = await supabase.rpc('get_ai_history_delta', {
      p_org_id: orgId,
      p_limit: limit,
      p_version: previous[0]?.historyVersion ?? null,
    });
    if (!snapshot.error) {
      const delta = snapshot.data as {
        unchanged: boolean;
        version: string;
        records: Record<string, unknown>[] | null;
      };
      if (delta.unchanged) return previous;
      const metadata = delta.records ?? [];
      const byId = new Map(previous.map((record) => [String(record.id), record]));
      const changed = metadata.filter((row) => {
        const old = byId.get(String(row.id));
        return !old?.analysisVersion || old.analysisVersion !== row.analysis_version;
      });
      const details = changed.length
        ? (unwrap(
            await supabase
              .from('ai_analysis')
              .select('id,analysis')
              .eq('org_id', orgId)
              .in(
                'id',
                changed.map((row) => row.id),
              ),
          ) as Record<string, unknown>[])
        : [];
      const content = new Map((details ?? []).map((row) => [String(row.id), row.analysis]));
      return metadata.map((row) => {
        const old = byId.get(String(row.id));
        const record = mapRecord(row);
        record.historyVersion = delta.version;
        record.analysis = content.has(String(row.id))
          ? mapRecord({ ...row, analysis: content.get(String(row.id)) }).analysis
          : old && old.analysisVersion === row.analysis_version
            ? old.analysis
            : null;
        // A deleted record between requests must not reuse stale content.
        if (!old || old.analysisVersion !== row.analysis_version)
          if (!content.has(String(row.id))) record.analysisVersion = undefined;
        return record;
      });
    }
    if (!['PGRST202', '42883'].includes(snapshot.error.code)) unwrap(snapshot);
  }

  const embedded = await supabase
    .from('ai_analysis')
    .select(
      'id, created_at, status, analysis, error_message, credit_used, duration, call_id, call_log!ai_call_same_org(call_time, caller_phone, caller_name, duration, recording_url)',
    )
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (!embedded.error) return ((embedded.data ?? []) as Record<string, unknown>[]).map((r) => mapRecord(r));

  // Only a missing relationship needs the manual join; don't amplify network/auth failures.
  if (!['PGRST200', 'PGRST201'].includes(embedded.error.code)) unwrap(embedded);

  // Relationship missing (PGRST200) — join by hand.
  const rows = unwrap(
    await supabase
      .from('ai_analysis')
      .select('id, created_at, status, analysis, error_message, credit_used, duration, call_id')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(limit),
  ) as Record<string, unknown>[];
  const ids = [...new Set((rows ?? []).map((r) => r.call_id).filter(Boolean))];
  const calls = ids.length
    ? ((unwrap(
        await supabase
          .from('call_log')
          .select('id, call_time, caller_phone, caller_name, duration, recording_url')
          .eq('org_id', orgId)
          .in('id', ids),
      ) as Record<string, unknown>[]) ?? [])
    : [];
  const byId = new Map(calls.map((c) => [String(c.id), c]));
  return (rows ?? []).map((r) => mapRecord(r, byId.get(String(r.call_id)) ?? null));
}

const ANALYSIS_COLUMNS = 'id,call_id,status,analysis,error_message,credit_used,duration,created_at';

export async function getAnalysisForCall(callId: number): Promise<AiRecord | null> {
  const row = unwrap(
    await supabase.from('ai_analysis').select(ANALYSIS_COLUMNS).eq('call_id', callId).maybeSingle(),
  ) as Record<string, unknown> | null;
  return row ? mapRecord(row, null) : null;
}

/** One request for every visible call instead of one per row. */
export async function listAnalysesForCalls(callIds: number[]): Promise<Record<number, AiRecord>> {
  if (!callIds.length) return {};
  const rows = unwrap(
    await supabase.from('ai_analysis').select(ANALYSIS_COLUMNS).in('call_id', callIds),
  ) as Record<string, unknown>[];
  const out: Record<number, AiRecord> = {};
  for (const row of rows ?? []) {
    const record = mapRecord(row, null);
    out[record.callId] = record;
  }
  return out;
}

/** A database trigger picks up pending rows and runs the analysis. */
export async function queueAnalysis(orgId: string, callId: number): Promise<void> {
  unwrap(
    await supabase
      .from('ai_analysis')
      .upsert(
        { call_id: callId, org_id: orgId, status: 'pending', attempted: true },
        { onConflict: 'call_id' },
      ),
  );
}

export function suggestedLabels(analysis: AiAnalysis | null): string[] {
  const raw = analysis?.labels;
  if (Array.isArray(raw))
    return raw
      .map(String)
      .map((l) => l.trim())
      .filter(Boolean);
  if (typeof raw === 'string')
    return raw
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean);
  return [];
}

export function suggestedTasks(analysis: AiAnalysis | null): string[] {
  const raw = analysis?.tasks ?? analysis?.todo;
  const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return items
    .map((t) =>
      typeof t === 'string'
        ? t
        : ((t as Record<string, unknown>)?.description ?? (t as Record<string, unknown>)?.title ?? ''),
    )
    .map((t) => String(t).trim())
    .filter(Boolean);
}
